import { useState, useEffect, createContext, useContext, useCallback } from 'react'
import { authAPI, ensureValidAccessToken } from '../lib/api'
import toast from 'react-hot-toast'

const AuthContext = createContext()

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [token, setToken] = useState(null)

  const logout = useCallback(async () => {
    // Try to revoke refresh token on backend
    const refreshToken = localStorage.getItem('refreshToken')
    if (refreshToken) {
      try {
        await authAPI.logout(refreshToken)
      } catch (e) {
        // Ignore errors - continue with logout anyway
      }
    }
    
    // Clear all stored auth data
    localStorage.removeItem('token')
    localStorage.removeItem('refreshToken')
    localStorage.removeItem('user')
    setUser(null)
    setToken(null)
  }, [])

  useEffect(() => {
    const initAuth = async () => {
      try {
        const token = localStorage.getItem('token')
        const refreshToken = localStorage.getItem('refreshToken')
        const storedUser = localStorage.getItem('user')
        
        // If user has token but no refresh token, they need to login again
        // (this happens for users who logged in before refresh token implementation)
        if (token && storedUser && !refreshToken) {
          localStorage.removeItem('token')
          localStorage.removeItem('user')
          setLoading(false)
          return
        }
        
        if (token && storedUser && refreshToken) {
          const parsedUser = JSON.parse(storedUser)
          setUser(parsedUser)
          setToken(token)

          // Renew access token before pages/SSE fire parallel requests (avoids 401 race on F5)
          const validToken = await ensureValidAccessToken()
          if (validToken) {
            setToken(validToken)
          } else if (!localStorage.getItem('token')) {
            setUser(null)
            setToken(null)
            setLoading(false)
            return
          }

          setLoading(false)

          // Fetch fresh user data in background to get updated assigned_location_id and password status
          try {
            const response = await authAPI.getProfile()
            if (response.data) {
              setUser(response.data)
              localStorage.setItem('user', JSON.stringify(response.data))
            }
          } catch (profileError) {
            // If profile fetch fails with password change required, update user state
            if (profileError.response?.status === 403 && profileError.response?.data?.requiresPasswordChange) {
              const updatedUser = { ...parsedUser, is_temporary_password: true }
              setUser(updatedUser)
              localStorage.setItem('user', JSON.stringify(updatedUser))
            }
            // Otherwise continue with stored user data
          }
        } else {
          setLoading(false)
        }
      } catch (error) {
        localStorage.removeItem('token')
        localStorage.removeItem('refreshToken')
        localStorage.removeItem('user')
        setLoading(false)
      }
    }

    initAuth()

    // Listen for password change required events from API interceptor
    const handlePasswordChangeRequired = (event) => {
      setUser(event.detail)
    }
    window.addEventListener('passwordChangeRequired', handlePasswordChangeRequired)
    
    // Listen for token expiration events from API interceptor
    const handleTokenExpired = () => {
      // Token expired - logout automatically
      logout()
    }
    window.addEventListener('tokenExpired', handleTokenExpired)

    return () => {
      window.removeEventListener('passwordChangeRequired', handlePasswordChangeRequired)
      window.removeEventListener('tokenExpired', handleTokenExpired)
    }
  }, [logout])

  const login = async (credentials) => {
    try {
      const response = await authAPI.login(credentials)
      const { token, accessToken, refreshToken, user, requiresPasswordChange } = response.data
      
      // Use accessToken if available, otherwise fall back to token (backward compatibility)
      const tokenToStore = accessToken || token
      
      localStorage.setItem('token', tokenToStore)
      if (refreshToken) {
        localStorage.setItem('refreshToken', refreshToken)
      }
      localStorage.setItem('user', JSON.stringify(user))
      setUser(user)
      setToken(tokenToStore)
      setLoading(false) // Ensure loading is false after successful login
      
      // Fetch complete user profile with permissions immediately after login
      // This ensures the menu updates correctly with permissions
      try {
        const profileResponse = await authAPI.getProfile()
        if (profileResponse.data) {
          const userWithPermissions = profileResponse.data
          // Ensure permission_codes is set for easier access
          if (userWithPermissions.permissions && !userWithPermissions.permission_codes) {
            userWithPermissions.permission_codes = userWithPermissions.permissions.map(p => p.code) || []
          }
          setUser(userWithPermissions)
          localStorage.setItem('user', JSON.stringify(userWithPermissions))
          
          // Dispatch custom event to notify Layout component
          window.dispatchEvent(new CustomEvent('userPermissionsUpdated', { 
            detail: userWithPermissions 
          }))
        }
      } catch (profileError) {
        // If profile fetch fails, continue with basic user data
        // The profile will be fetched on next page load via initAuth
      }
      
      return { success: true, requiresPasswordChange }
    } catch (error) {
      const message = error.response?.data?.error || 'Login error'
      return { success: false, error: message }
    }
  }

  const updateUser = (userData) => {
    setUser(userData)
    localStorage.setItem('user', JSON.stringify(userData))
  }

  /** Set session after password reset (auto-login). Same shape as login response. */
  const setSession = useCallback(({ accessToken, token, refreshToken, user: userData }) => {
    const t = accessToken || token
    if (t) localStorage.setItem('token', t)
    if (refreshToken) localStorage.setItem('refreshToken', refreshToken)
    if (userData) {
      setUser(userData)
      localStorage.setItem('user', JSON.stringify(userData))
    }
    setToken(t)
  }, [])

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    updateUser,
    setSession,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
